import React, { useCallback, useMemo, useRef, useState } from "react";
import { OverlaysProvider } from "contexts/OverlaysProvider";
import { OverlayType, OverlayData } from "contexts/OverlayContext";
import { PlainModal } from "@design-system/Modal/Modal";
import { ChildrenProps } from "../../types";
import { Settings } from "./Settings";
import { useValidSettingsPages } from "./useValidSettingsPages";
import { SettingsContext } from "./SettingsProvider";
import { resolveSettingsPathSegment } from "./constants";
import { resolveActiveSettingsPath } from "./resolveActiveSettingsPath";

const DEFAULT_PATH = ["general"];

interface SettingsProviderProps extends ChildrenProps {
    openGlobalOverlay?: <T extends OverlayType>(
        type: T,
        data: OverlayData<T>,
    ) => void;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
    children,
    openGlobalOverlay,
}) => {
    const [openedPath, setOpenedPath] = useState<string[]>([]);
    const [isOpened, setIsOpened] = useState(false);
    const [entityId, setEntityId] = useState<number>();
    const isOpenedRef = useRef(false);
    const validPaths = useValidSettingsPages();
    const path = useMemo(
        () =>
            resolveActiveSettingsPath({ pathSegments: openedPath, validPaths }),
        [openedPath, validPaths],
    );

    const open = useCallback(
        (newPath: string[] = DEFAULT_PATH) => {
            isOpenedRef.current = true;
            const resolvedPath = [
                resolveSettingsPathSegment(newPath[0]),
                ...newPath.slice(1),
            ];
            if ((validPaths as string[]).includes(resolvedPath[0])) {
                setOpenedPath(resolvedPath);

                const entityIdMatch = /(\d+)$/.exec(resolvedPath[0]);

                if (entityIdMatch) {
                    setEntityId(parseInt(entityIdMatch[0], 10));
                } else {
                    setEntityId(undefined);
                }
            } else {
                setOpenedPath(DEFAULT_PATH);
                setEntityId(undefined);
            }
            setIsOpened(true);
        },
        [validPaths],
    );

    const [disableAnimationState, setDisableAnimationState] = useState(false);

    const closeWithDisabledAnimation = useCallback(
        (onAfterClose?: () => void) => {
            // NOTE: in case of stacked modals,
            // to prevent page unresponsiveness cause of missing animation events
            // we need to disable animation
            isOpenedRef.current = false;
            setDisableAnimationState(true);
            setTimeout(() => {
                setIsOpened(false);
                onAfterClose?.();
            }, 5);
            setTimeout(() => {
                setDisableAnimationState(false);
            }, 500);
        },
        [],
    );

    const close = useCallback(
        (disableAnimation?: boolean) => {
            isOpenedRef.current = false;
            if (disableAnimation) {
                closeWithDisabledAnimation();
            } else {
                setIsOpened(false);
            }
        },
        [closeWithDisabledAnimation],
    );

    const changePage = useCallback(
        (newPath: string[]) => {
            if (!isOpenedRef.current) {
                return;
            }
            open(newPath);
        },
        [open],
    );

    const closeWithOverlay = useCallback(
        <T extends OverlayType>(
            overlayType: T,
            overlayData: OverlayData<T>,
        ) => {
            if (isOpenedRef.current) {
                closeWithDisabledAnimation(() => {
                    openGlobalOverlay?.(overlayType, overlayData);
                });
            } else {
                openGlobalOverlay?.(overlayType, overlayData);
            }
        },
        [closeWithDisabledAnimation, openGlobalOverlay],
    );

    const settingsContextValue = useMemo(
        () => ({
            path,
            isOpened,
            open,
            changePage,
            entityId,
            close,
            closeWithOverlay,
            validPaths,
        }),
        [
            entityId,
            isOpened,
            open,
            changePage,
            path,
            close,
            closeWithOverlay,
            validPaths,
        ],
    );

    return (
        <SettingsContext.Provider value={settingsContextValue}>
            {children}

            <PlainModal
                className="settings-widget__modal"
                dialogClassName="settings-widget__dialog"
                contentClassName="settings-widget__content"
                show={isOpened}
                onHide={close}
                disableAnimation={disableAnimationState}
            >
                <OverlaysProvider>
                    <Settings />
                </OverlaysProvider>
            </PlainModal>
        </SettingsContext.Provider>
    );
};
